<template>
  <div :test_id="`${test_id}-wrapper`">
    <FormLabel
      v-if="$slots.label"
      :tooltip="tooltip"
      :is-required="isRequired"
      :id="id"
      class="mb-2"
    >
      <slot name="label" />
    </FormLabel>

    <ComboboxRoot
      v-model:open="isOpen"
      :name="id"
      class="relative"
      :disabled="disabled"
    >
      <ComboboxAnchor>
        <ComboboxTrigger
          tabindex="0"
          :class="
            twMerge(
              'focus:ring-primary-200 focus:border-primary-400 relative flex w-full cursor-default items-center justify-between overflow-hidden rounded-xl border bg-neutral-50 py-3 pr-10 pl-4 text-left leading-5 text-neutral-700 shadow-sm outline-none focus:bg-white focus:ring-2 disabled:opacity-50',
              hasErrors ? 'border-error-400 bg-white' : 'border-neutral-200',
              isOpen
                ? 'ring-primary-200 border-primary-400 bg-white ring-2'
                : '',
            )
          "
        >
          <span
            class="block truncate"
            :class="{ 'text-neutral-600': !selected }"
          >
            {{ selected?.label || $attrs.placeholder || '&nbsp;' }}
          </span>
          <div
            class="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="h-5 w-5 text-neutral-400"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="m19.5 8.25-7.5 7.5-7.5-7.5"
              />
            </svg>
          </div>
        </ComboboxTrigger>
      </ComboboxAnchor>

      <ComboboxPortal>
        <ComboboxContent
          class="ring-opacity-5 z-2000 max-h-72 w-full overflow-hidden rounded-xl bg-white p-2 text-base shadow-lg ring-1 ring-neutral-200 focus:outline-none sm:text-sm"
          :style="{
            width: 'var(--reka-popper-anchor-width)',
            scrollbarWidth: 'thin',
          }"
          position="popper"
          :side-offset="8"
          :collision-padding="10"
          avoid-collisions
          prioritize-position
          side-flip
          align="start"
          :test_id="`${test_id}-main`"
        >
          <div v-if="searchable" class="mb-2">
            <ComboboxInput
              v-model="searchQuery"
              class="w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm leading-5 text-neutral-900 outline-none placeholder:text-neutral-500 focus:ring-0 focus:outline-none"
              :placeholder="($attrs.searchPlaceholder as string) || 'Search...'"
              @keydown.enter.prevent
              :test_id="test_id"
            />
          </div>

          <ComboboxViewport style="scrollbar-width: thin">
            <ComboboxVirtualizer
              v-slot="{ option }"
              :options="filteredOptions"
              :estimate-size="40"
              :text-content="(option) => option.label"
            >
              <ComboboxItem
                :key="option.value.toString()"
                :value="option"
                class="relative my-1 w-full cursor-default rounded-lg px-3 py-2 text-neutral-900 outline-none select-none"
                :class="[
                  selected?.value === option.value
                    ? `bg-secondary-50 text-secondary-500 ${allowEmpty ? 'data-highlighted:bg-error-100 data-highlighted:text-error-500 hover:bg-error-100 hover:text-error-500' : ''}`
                    : 'hover:bg-neutral-100 hover:text-neutral-900 data-highlighted:bg-neutral-100 data-highlighted:text-neutral-900',
                ]"
                @select="(event) => handleSelect(event, option)"
              >
                <span
                  class="block truncate"
                  :class="[
                    selected?.value === option.value
                      ? 'font-medium'
                      : 'font-normal',
                  ]"
                >
                  {{ option.label }}
                </span>
              </ComboboxItem>
            </ComboboxVirtualizer>
          </ComboboxViewport>
        </ComboboxContent>
      </ComboboxPortal>
    </ComboboxRoot>

    <div v-if="hasErrors || $slots.caption" class="mt-2">
      <FormErrors v-if="hasErrors" :id="id" :errors="errors" />
      <FormCaption v-if="$slots.caption" :class="{ 'mt-1': hasErrors }">
        <slot name="caption" />
      </FormCaption>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ComboboxRoot,
  ComboboxInput,
  ComboboxTrigger,
  ComboboxContent,
  ComboboxItem,
  ComboboxViewport,
  ComboboxPortal,
  ComboboxAnchor,
  ComboboxVirtualizer,
  useFilter,
} from 'reka-ui'
import { computed, ref, useId, watch } from 'vue'
import FormLabel from './FormLabel.vue'
import FormErrors from './FormErrors.vue'
import FormCaption from './FormCaption.vue'
import type { FormOptionType } from '@/shared/types'
import { twMerge } from 'tailwind-merge'

type Props = {
  options: FormOptionType[]
  isError?: boolean
  errors?: string | string[]
  isRequired?: boolean
  tooltip?: string
  allowEmpty?: boolean
  disabled?: boolean
  searchable?: boolean
  test_id?: string
}

const props = withDefaults(defineProps<Props>(), {
  isError: false,
  errors: '',
  isRequired: false,
  tooltip: '',
  allowEmpty: false,
  disabled: false,
  searchable: false,
})

const modelValue = defineModel<FormOptionType['value'] | null>({
  required: true,
})

const emit = defineEmits<{
  (e: 'input', value: FormOptionType['value'] | null): void
}>()

const id = `growthmentor-select-${useId()}`
const searchQuery = ref('')
const isOpen = ref(false)

const selected = ref<FormOptionType | null | undefined>(
  props.options.find((option) => option.value === modelValue.value),
)

const hasErrors = computed(() => {
  if (!props.isError) return false
  const isString = typeof props.errors === 'string'
  return isString ? props.errors : (props.errors as string[]).length
})

const { contains } = useFilter({ sensitivity: 'base' })
const filteredOptions = computed(() =>
  props.searchable
    ? props.options.filter((option) =>
        contains(option.label, searchQuery.value),
      )
    : props.options,
)

const handleSelect = (event: Event, option: FormOptionType) => {
  if (props.allowEmpty && selected.value?.value === option.value) {
    event.preventDefault()
    modelValue.value = null
    isOpen.value = false
  } else {
    modelValue.value = option.value
    isOpen.value = false
  }
}

watch(
  () => modelValue.value,
  (newValue) => {
    emit('input', modelValue.value)
    if (selected.value && selected.value.value === newValue) return
    const found = props.options.find((option) => option.value === newValue)
    selected.value = found || null
  },
)

watch(isOpen, (newValue) => {
  if (!newValue) searchQuery.value = ''
})
</script>
